import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  User,
  MessageSquare,
  CheckCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });
    setIsSuccess(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      lines: ["1368-B S Military Trail, Suite R", "West Palm Beach, FL 33415"],
    },
    {
      icon: Phone,
      title: "Call Us",
      lines: ["786-991-8919"],
      href: "tel:786-991-8919",
    },
    {
      icon: Mail,
      title: "Email Us",
      lines: ["espadywisellc@gmail.com"],
      href: "mailto:espadywisellc@gmail.com",
    },
    {
      icon: Clock,
      title: "Business Hours",
      lines: ["Mon - Fri: 9:00 AM - 6:00 PM", "Sat: 10:00 AM - 4:00 PM"],
    },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-[#0F392B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              CONTACT US
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              Get In Touch
            </h1>
            <p className="font-body text-white/80 max-w-xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and
              we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  data-testid={`contact-info-${index}`}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#0F392B]/10 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-[#0F392B]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-2">
                        {info.title}
                      </h3>
                      {info.lines.map((line, lineIndex) =>
                        info.href ? (
                          <a
                            key={lineIndex}
                            href={info.href}
                            className="block font-body text-[#4A5568] hover:text-[#D4AF37] transition-colors"
                          >
                            {line}
                          </a>
                        ) : (
                          <p
                            key={lineIndex}
                            className="font-body text-[#4A5568]"
                          >
                            {line}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSuccess ? (
                <div
                  data-testid="contact-success"
                  className="bg-white rounded-2xl p-12 shadow-xl text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="font-heading text-3xl font-bold text-[#0F392B] mb-4">
                    Message Sent!
                  </h2>
                  <p className="font-body text-[#4A5568] mb-8">
                    Thank you for reaching out. We'll get back to you as soon as
                    possible.
                  </p>
                  <Button
                    onClick={resetForm}
                    data-testid="send-another-btn"
                    className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-3"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h2 className="font-heading text-2xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                    <MessageSquare className="w-6 h-6 text-[#D4AF37]" />
                    Send us a Message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="name"
                          className="font-body text-sm text-[#4A5568] mb-2 block"
                        >
                          Full Name *
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="John Doe"
                            required
                            data-testid="contact-input-name"
                            className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                          />
                        </div>
                      </div>

                      <div>
                        <Label
                          htmlFor="email"
                          className="font-body text-sm text-[#4A5568] mb-2 block"
                        >
                          Email Address *
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="john@example.com"
                            required
                            data-testid="contact-input-email"
                            className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="phone"
                          className="font-body text-sm text-[#4A5568] mb-2 block"
                        >
                          Phone Number
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                            data-testid="contact-input-phone"
                            className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                          />
                        </div>
                      </div>

                      <div>
                        <Label
                          htmlFor="subject"
                          className="font-body text-sm text-[#4A5568] mb-2 block"
                        >
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="How can we help?"
                          required
                          data-testid="contact-input-subject"
                          className="h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                        />
                      </div>
                    </div>

                    <div>
                      <Label
                        htmlFor="message"
                        className="font-body text-sm text-[#4A5568] mb-2 block"
                      >
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us more about your inquiry..."
                        rows={6}
                        required
                        data-testid="contact-input-message"
                        className="border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-contact-btn"
                      className="w-full sm:w-auto bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              OUR LOCATION
            </span>
            <h2 className="font-heading text-3xl font-bold text-[#0F392B] mb-4">
              Visit Our Office
            </h2>
            <div className="gold-line mx-auto" />
          </div>
          <div className="map-container rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3565.9!2d-80.0897!3d26.6425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d8d6c4e3a0b3b1%3A0x1234567890abcdef!2s1368%20S%20Military%20Trail%2C%20West%20Palm%20Beach%2C%20FL%2033415!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Espady Multi-Service Co. Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
